import { findKey } from 'lodash';

import { HIDDEN_LANGUAGE, INFORMAL_LANGUAGE, LANGUAGE } from './const';

const LANGUAGES = Object.values(LANGUAGE);
const HIDDEN_LANGUAGES = Object.values(HIDDEN_LANGUAGE);

export const LANGUAGE_LABELS = {
  [LANGUAGE.bg]: 'Bulgarian',
  [LANGUAGE.zh_tw]: 'Chinese (Traditional)',
  [LANGUAGE.zh_cn]: 'Chinese (Simplified)',
  [LANGUAGE.cs]: 'Czech',
  [LANGUAGE.da]: 'Danish',
  [LANGUAGE.de]: 'German (formal)',
  [LANGUAGE.de_in]: 'German (informal)',
  [LANGUAGE.de_ch]: 'Swiss German',
  [LANGUAGE.el]: 'Greek',
  [LANGUAGE.en_us]: 'English (US)',
  [LANGUAGE.en_gb]: 'English (UK)',
  [LANGUAGE.es]: 'Spanish',
  [LANGUAGE.es_cs]: 'Spanish (Castilian)',
  [LANGUAGE.es_la]: 'Spanish (Latin America)',
  [LANGUAGE.fr_qu]: 'French (Quebecois)',
  [LANGUAGE.fr]: 'French',
  [LANGUAGE.hu]: 'Hungarian',
  [LANGUAGE.it]: 'Italian',
  [HIDDEN_LANGUAGE.he]: 'Hebrew',
  [LANGUAGE.ja]: 'Japanese',
  [LANGUAGE.nl]: 'Dutch',
  [LANGUAGE.pl]: 'Polish',
  [LANGUAGE.pt_pt]: 'Portuguese',
  [LANGUAGE.pt_br]: 'Portuguese (BR)',
  [LANGUAGE.ro]: 'Romanian',
  [LANGUAGE.ru]: 'Russian',
  [LANGUAGE.cmn]: 'Chinese (Mandarin)',
  [LANGUAGE.yue]: 'Chinese (Cantonese)',
  [LANGUAGE.sv]: 'Swedish',
  [LANGUAGE.th]: 'Thai',
  [LANGUAGE.ms]: 'Malay',
  [LANGUAGE.tr]: 'Turkish',
};

export const LANGUAGE_LABELS_WITH_EMOJIS = {
  [LANGUAGE.bg]: '🇧🇬 Bulgarian',
  [LANGUAGE.cs]: '🇨🇿 Czech',
  [LANGUAGE.zh_cn]: '🇨🇳 Chinese (Simplified)',
  [LANGUAGE.zh_tw]: '🇨🇳 Chinese (Traditional)',
  [LANGUAGE.th]: '🇹🇭 Thai',
  [LANGUAGE.ms]: '🇲🇾 Malay',
  [LANGUAGE.da]: '🇩🇰 Danish',
  [LANGUAGE.de]: '🇩🇪 German (formal)',
  [LANGUAGE.de_in]: '🇩🇪 German (informal)',
  [LANGUAGE.el]: '🇬🇷 Greek',
  [LANGUAGE.en_us]: '🇺🇸 English',
  [LANGUAGE.en_gb]: '🇬🇧 English UK',
  [LANGUAGE.es]: '🇪🇸 Spanish',
  [LANGUAGE.es_la]: '🇲🇽 Spanish (LA)',
  [LANGUAGE.es_cs]: '🇪🇸 Spanish (Castilian)',
  [LANGUAGE.fr]: '🇫🇷 French',
  [LANGUAGE.fr_qu]: '🇨🇦 French (Quebecois)',
  [LANGUAGE.hu]: '🇭🇺 Hungarian',
  [LANGUAGE.it]: '🇮🇹 Italian',
  [HIDDEN_LANGUAGE.he]: '🇮🇱 Hebrew',
  [LANGUAGE.ja]: '🇯🇵 Japanese',
  [LANGUAGE.nl]: '🇳🇱 Dutch',
  [LANGUAGE.pl]: '🇵🇱 Polish',
  [LANGUAGE.pt_pt]: '🇵🇹 Portuguese',
  [LANGUAGE.pt_br]: '🇧🇷 Portuguese (BR)',
  [LANGUAGE.ro]: '🇷🇴 Romanian',
  [LANGUAGE.ru]: '🇷🇺 Russian',
  [LANGUAGE.cmn]: '🇨🇳 Chinese (Mandarin)',
  [LANGUAGE.yue]: '🇨🇳 Chinese (Cantonese)',
  [LANGUAGE.sv]: '🇸🇪 Swedish',
  [LANGUAGE.tr]: '🇹🇷 Turkish',
};

export const LANGUAGE_CHIP_LABELS = {
  [LANGUAGE.bg]: 'BG',
  [LANGUAGE.cs]: 'CS',
  [LANGUAGE.zh_cn]: 'CH',
  [LANGUAGE.zh_tw]: 'CH',
  [LANGUAGE.da]: 'DA',
  [LANGUAGE.nl]: 'NL',
  [LANGUAGE.en_us]: 'EN-US',
  [LANGUAGE.en_gb]: 'EN-UK',
  [LANGUAGE.fr]: 'FR',
  [LANGUAGE.fr_qu]: 'FR-QU',
  [LANGUAGE.de]: 'DE',
  [LANGUAGE.de_in]: 'DE',
  [LANGUAGE.de_ch]: 'DE-CH',
  [LANGUAGE.el]: 'EL',
  [HIDDEN_LANGUAGE.he]: 'HE',
  [LANGUAGE.hu]: 'HU',
  [LANGUAGE.it]: 'IT',
  [LANGUAGE.ja]: 'JA',
  [LANGUAGE.ms]: "MS",
  [LANGUAGE.pl]: 'PL',
  [LANGUAGE.pt_pt]: 'PT',
  [LANGUAGE.pt_br]: 'PT',
  [LANGUAGE.ro]: 'RO',
  [LANGUAGE.ru]: 'RU',
  [LANGUAGE.es]: 'ES',
  [LANGUAGE.es_cs]: 'ES-CS',
  [LANGUAGE.es_la]: 'ES-LA',
  [LANGUAGE.cmn]: 'CMN',
  [LANGUAGE.sv]: 'SV',
  [LANGUAGE.th]: 'TH',
  [LANGUAGE.tr]: 'TR',
  [LANGUAGE.yue]: 'YUE',
};

export const AVAILABLE_LANGUAGES_FOR_BLOG = [
  LANGUAGE.bg,
  LANGUAGE.cs,
  LANGUAGE.yue,
  LANGUAGE.cmn,
  LANGUAGE.zh_tw,
  LANGUAGE.zh_cn,
  LANGUAGE.da,
  LANGUAGE.nl,
  LANGUAGE.en_us,
  LANGUAGE.en_gb,
  LANGUAGE.fr,
  LANGUAGE.fr_qu,
  LANGUAGE.de,
  LANGUAGE.el,
  HIDDEN_LANGUAGE.he,
  LANGUAGE.hu,
  LANGUAGE.it,
  LANGUAGE.ja,
  LANGUAGE.ms,
  LANGUAGE.pl,
  LANGUAGE.pt_pt,
  LANGUAGE.pt_br,
  LANGUAGE.ro,
  LANGUAGE.ru,
  LANGUAGE.es,
  LANGUAGE.es_la,
  LANGUAGE.es_cs,
  LANGUAGE.sv,
  LANGUAGE.de_ch,
  LANGUAGE.th,
  LANGUAGE.tr,
];

export const LANGUAGE_KEY_INFORMAL = '_in';
export const LANGUAGE_SUFFIX_FORMAL = '(formal)';

export const removeSuffix = (language, suffix) => {
  if (!language) return;

  if (language.endsWith(suffix)) {
    return language.slice(0, -suffix.length);
  }
  return language;
};

export const getLanguageWithoutFormality = (language) => {
  return removeSuffix(language, LANGUAGE_KEY_INFORMAL);
};

export const getLanguageWithoutFormal = (language) => {
  return removeSuffix(language, LANGUAGE_SUFFIX_FORMAL);
};

const TARGET_FORMALITY = {
  formal: 'more', // backend value
  informal: 'less', // backend value
};

export const LANGUAGE_BACKEND_EN = 'en';
export const LANGUAGE_BACKEND_PT = 'pt';

export const LANGUAGE_TARGET_FORMALITY = {
  [LANGUAGE.de]: TARGET_FORMALITY.formal,
  [LANGUAGE.de_in]: TARGET_FORMALITY.informal,
};

export const getLanguageTargetFormality = (language) => {
  return LANGUAGE_TARGET_FORMALITY[language];
};

export const getLanguageFromTargetFormality = (language, languageTargetFormality) => {
  return languageTargetFormality === TARGET_FORMALITY.informal
    ? findKey(INFORMAL_LANGUAGE, (value) => language === value)
    : language;
};

export const normalizeLanguage = (nonAligned) => {
  return INFORMAL_LANGUAGE[nonAligned] || nonAligned;
};

const LANGUAGE_ALIASES = {
  [LANGUAGE_BACKEND_EN]: LANGUAGE.en_us,
  [LANGUAGE_BACKEND_PT]: LANGUAGE.pt_pt,
};
export const alignLanguage = (nonAligned, languageTargetFormality = null) => {
  if (LANGUAGES.includes(nonAligned)) {
    return languageTargetFormality
      ? getLanguageFromTargetFormality(nonAligned, languageTargetFormality)
      : nonAligned;
  }

  if (HIDDEN_LANGUAGES.includes(nonAligned)) {
    return nonAligned;
  }

  if (LANGUAGE_ALIASES[nonAligned]) {
    return LANGUAGE_ALIASES[nonAligned];
  }

  return null;
};

export const getLanguageLabel = (language) => LANGUAGE_LABELS[language];

export const getLanguageLabelWithEmoji = (language) => LANGUAGE_LABELS_WITH_EMOJIS[language];

export const getLanguageChipLabel = (language) => LANGUAGE_CHIP_LABELS[language];

export const isNonEnglish = (language) => {
  // Treat "empty" as English
  if (!language) {
    return false;
  }

  return !language.startsWith(LANGUAGE_BACKEND_EN);
};

export const isSameLanguage = (lang1, lang2) => {
  if (!lang1 || !lang2) {
    return false;
  }
  // Check if both are English variants
  if (!isNonEnglish(lang1) && !isNonEnglish(lang2)) {
    return true; // Consider all English variants as the same
  }

  const formalitySuffix = '_in';
  const baseLang1 = removeSuffix(lang1, formalitySuffix);
  const baseLang2 = removeSuffix(lang2, formalitySuffix);
  return baseLang1 === baseLang2;
};

export const isHebrewLanguage = (language) => language === HIDDEN_LANGUAGE.he;

export const isRtlLanguage = (language) => isHebrewLanguage(language);

export const NON_CAPITALIZED_LANGUAGES = [
  HIDDEN_LANGUAGE.he,
  LANGUAGE.yue,
  LANGUAGE.cmn,
  LANGUAGE.ja,
];

export const NO_FORMULA_LANGUAGES = [LANGUAGE.yue, LANGUAGE.cmn, LANGUAGE.ja];

const SUFFIX_TO_REMOVE = '(formal)';

export const brandVoiceLanguageErrorMessage = (language, asset) => {
  if (!language) {
    return '';
  }
  const languageLabel = getLanguageLabel(alignLanguage(language));
  const targetLanguage = removeSuffix(languageLabel, SUFFIX_TO_REMOVE);

  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const anOrA = vowels.includes(targetLanguage[0].toLowerCase()) ? 'an' : 'a';

  return `This ${asset} is being created for ${anOrA} ${targetLanguage} brand voice. Please make sure that this is written in ${targetLanguage} as well.`;
};

export const canCountCharactersForLanguage = (language) => {
  return NON_CAPITALIZED_LANGUAGES.includes(language) && language !== HIDDEN_LANGUAGE.he;
};

export const sortByLanguage = (parameterName, language) => {
  return (a, b) => {
    if (isSameLanguage(a[parameterName], language) && !isSameLanguage(b[parameterName], language))
      return -1;
    if (!isSameLanguage(a[parameterName], language) && isSameLanguage(b[parameterName], language))
      return 1;
    return 0;
  };
};
